// "Today" for the learner dashboard: India-time days, this week's activity, daily page goal, effective streak.
// Dependency-free so the backend (GET /api/gamification/today) and the apps (fallback before that endpoint is deployed)
// compute exactly the same numbers.

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const DAY_MS = 86_400_000;

export const DEFAULT_DAILY_PAGE_GOAL = 5;

/** Calendar day (YYYY-MM-DD) of an instant in India time: the day a streak is counted in (mirrors record_daily_activity). */
export function istDay(date = new Date()) {
  return new Date(new Date(date).getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

const dayNumber = (day) => Math.floor(Date.parse(`${day}T00:00:00Z`) / DAY_MS);

/**
 * What a learner should see for one stored streak row: alive only if they were active today or yesterday (India
 * time); after a missed day it reads 0 until they learn again. `active_today` means today already counts.
 */
export function effectiveStreak(row, now = new Date()) {
  const today = istDay(now);
  const last = row?.last_activity_date ? String(row.last_activity_date).slice(0, 10) : null;
  const best = Number(row?.best_count) || 0;
  if (!last) return { current_count: 0, best_count: best, active_today: false, at_risk: false, last_activity_date: null };
  const gap = dayNumber(today) - dayNumber(last);
  const stored = Number(row?.current_count) || 0;
  const alive = gap <= 1;
  return {
    current_count: alive ? Math.max(stored, 1) : 0,
    best_count: Math.max(best, alive ? stored : 0),
    active_today: gap <= 0,
    at_risk: gap === 1,
    last_activity_date: last,
  };
}

/** India-time today and this week (Monday first): UTC instants where they start, and the seven day keys. */
export function istWeek(now = new Date()) {
  const today = istDay(now);
  const todayStartUtc = new Date(Date.parse(`${today}T00:00:00Z`) - IST_OFFSET_MS);
  const weekday = (new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7;
  const weekStartUtc = new Date(todayStartUtc.getTime() - weekday * DAY_MS);
  const days = Array.from({ length: 7 }, (_, i) => istDay(new Date(weekStartUtc.getTime() + i * DAY_MS + 1)));
  return { today, todayStartUtc, weekStartUtc, days };
}

/** Pure assembly of the dashboard's "today" numbers from already-loaded rows. */
export function buildTodaySummary({ now = new Date(), activityTimestamps = [], completedTimestamps = [], xpRows = [], streakRow = null, xpRow = null, goal = DEFAULT_DAILY_PAGE_GOAL }) {
  const week = istWeek(now);
  const active = new Set(activityTimestamps.filter(Boolean).map((ts) => istDay(new Date(ts))));
  const startMs = week.todayStartUtc.getTime();
  const pagesToday = completedTimestamps.filter((ts) => ts && Date.parse(ts) >= startMs).length;
  const xpToday = xpRows.filter((row) => row?.created_at && Date.parse(row.created_at) >= startMs).reduce((sum, row) => sum + (Number(row.xp) || 0), 0);
  const streak = effectiveStreak(streakRow, now);
  if (pagesToday > 0) active.add(week.today);
  // The streak is recorded by the database from every learning surface (journeys included), so its days always count:
  // a live N-day streak lights the N days ending on its last active day, and the week never disagrees with it.
  if (streak.current_count > 0 && streak.last_activity_date) {
    const last = dayNumber(streak.last_activity_date);
    for (let i = 0; i < streak.current_count && i < 7; i += 1) active.add(new Date((last - i) * DAY_MS).toISOString().slice(0, 10));
  }
  return {
    today: week.today,
    week: week.days.map((day) => ({ day, active: active.has(day), isToday: day === week.today, future: day > week.today })),
    pagesToday,
    goal,
    goalMet: pagesToday >= goal,
    xpToday,
    streak: { current: streak.current_count, best: streak.best_count, activeToday: streak.active_today || pagesToday > 0, atRisk: streak.at_risk && pagesToday === 0 },
    level: Number(xpRow?.level_number) || 1,
    totalXp: Number(xpRow?.total_xp) || 0,
    xpToNextLevel: Number(xpRow?.xp_to_next_level) || 0,
    xpInLevel: Number(xpRow?.xp_in_current_level) || 0,
  };
}

/**
 * Loads this week's activity and today's completions/XP for one learner (any Supabase client: service role on the
 * server, the learner's own session on a device) and builds the summary. `tolerant` treats a failed table read as
 * empty instead of throwing (client fallback, where a table may not be readable).
 */
export async function loadTodaySummary({ supabase, userId, now = new Date(), tolerant = false }) {
  const { weekStartUtc, todayStartUtc } = istWeek(now);
  const since = weekStartUtc.toISOString();
  const results = await Promise.all([
    supabase.from('learner_sessions').select('started_at').eq('user_id', userId).gte('started_at', since),
    supabase.from('user_progress').select('last_accessed').eq('user_id', userId).gte('last_accessed', since),
    supabase.from('user_progress').select('completed_at').eq('user_id', userId).eq('status', 'completed').gte('completed_at', since),
    supabase.from('activity_attempts').select('started_at').eq('user_id', userId).gte('started_at', since),
    supabase.from('quiz_responses').select('attempted_on').eq('user_id', userId).gte('attempted_on', since),
    supabase.from('xp_awards').select('xp, created_at').eq('user_id', userId).gte('created_at', todayStartUtc.toISOString()),
    supabase.from('user_streaks').select('current_count, best_count, last_activity_date').eq('user_id', userId).eq('streak_type', 'daily_activity').maybeSingle(),
    supabase.from('user_xp').select('total_xp, level_number, xp_to_next_level, xp_in_current_level').eq('user_id', userId).maybeSingle(),
  ]);
  const data = results.map((result) => {
    if (result.error && !tolerant) throw result.error;
    return result.error ? null : result.data;
  });
  const [sessions, seen, done, attempts, quizzes, xp, streak, userXp] = data;
  const completed = (done || []).map((r) => r.completed_at);
  return buildTodaySummary({
    now,
    activityTimestamps: [
      ...(sessions || []).map((r) => r.started_at),
      ...(seen || []).map((r) => r.last_accessed),
      ...completed,
      ...(attempts || []).map((r) => r.started_at),
      ...(quizzes || []).map((r) => r.attempted_on),
    ],
    completedTimestamps: completed,
    xpRows: xp || [],
    streakRow: streak,
    xpRow: userXp,
  });
}
